// Formato de código de producto: prefijo + secuencia correlativa (con relleno de ceros).
// Inventario → Catálogo → Formato de código de producto.
import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Hash, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, invRpc } from './shared';

interface Secuencia {
  prefijo: string;
  relleno: number;
  ultimo: number;
  ejemplo: string;
}

const QK = ['inv-prod-sec'];

export function CodigosProducto() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Secuencia>({
    queryKey: QK,
    queryFn: () => invRpc<Secuencia>('inv_producto_secuencia_get', {}),
  });

  const [prefijo, setPrefijo] = useState('');
  const [relleno, setRelleno] = useState(6);
  const [desde, setDesde] = useState('');

  useEffect(() => {
    if (data) {
      setPrefijo(data.prefijo ?? '');
      setRelleno(data.relleno ?? 6);
    }
  }, [data]);

  const ejemploPreview = useMemo(() => {
    if (!data) return '';
    const rell = Number.isFinite(relleno) && relleno > 0 ? relleno : 1;
    const base = desde !== '' && Number.isFinite(Number(desde)) ? Number(desde) : data.ultimo + 1;
    return `${prefijo}${String(base).padStart(rell, '0')}`;
  }, [data, prefijo, relleno, desde]);

  const guardar = useMutation({
    mutationFn: () =>
      invRpc<Secuencia>('inv_producto_secuencia_set', {
        _prefijo: prefijo,
        _relleno: relleno,
        _desde: desde === '' ? null : Number(desde),
      }),
    onSuccess: (r) => {
      toast.success(`Formato guardado. Próximo código: ${r.ejemplo}`);
      setDesde('');
      void qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4 max-w-xl">
      <h1 className="text-xl font-semibold flex items-center gap-2">
        <Hash className="w-5 h-5" /> Formato de código de producto
      </h1>

      {isLoading ? (
        <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>
      ) : (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Configuración</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <p className="text-xs text-muted-foreground">
              Los productos nuevos toman un código correlativo con este formato. Ejemplo del
              próximo código: <span className="font-mono font-semibold text-foreground">{ejemploPreview}</span>.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <Campo label="Prefijo">
                <input
                  className={fieldCls}
                  maxLength={10}
                  placeholder="Ej. P-"
                  value={prefijo}
                  onChange={(e) => setPrefijo(e.target.value)}
                />
              </Campo>
              <Campo label="Dígitos">
                <input
                  type="number"
                  min={1}
                  max={12}
                  className={fieldCls}
                  value={relleno}
                  onChange={(e) => setRelleno(Math.min(12, Math.max(1, Number(e.target.value) || 1)))}
                />
              </Campo>
            </div>

            <Campo label="Reiniciar contador desde (opcional)">
              <input
                type="number"
                min={0}
                className={fieldCls}
                placeholder="Dejar vacío para no reiniciar"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
              />
            </Campo>
            <p className="text-xs text-muted-foreground -mt-2">
              Déjalo vacío para no reiniciar. Úsalo con cuidado: podría generar códigos repetidos si retrocedes.
            </p>

            <div className="flex justify-end border-t pt-3">
              <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
